import { InfoIcon } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useT } from '@/lib/i18n';

/**
 * The detail behind a field, on demand rather than under every input.
 *
 * ### Why this exists
 *
 * The console explains itself at length, and that was right while it was being
 * built: the paragraph under a control recorded *why* the control behaves as it
 * does, for a reader who was mostly the person building the next screen. It
 * reads very differently to an operator. "Applies to the console only. What
 * language a candidate is assessed in is fixed when their assessment is
 * generated, and nothing here can change it" is three clauses of reassurance
 * about something the reader had not yet worried about.
 *
 * So the rule from here: **a field says what it is; the reasoning goes behind
 * this icon.** Nothing is deleted — an operator who wonders "does this change
 * what a candidate sees?" still gets the exact answer — it simply stops being
 * the loudest thing on the screen.
 *
 * ### What still belongs as visible text
 *
 * A consequence the operator must see *before* acting, not after wondering. A
 * warning that publishing mints a version, or that an import is about to
 * overwrite a catalogue, is not a hint — it is part of the decision, and hiding
 * it behind a hover would be the same mistake in the other direction.
 */
export function InfoHint({ children, label }: { children: string; label?: string }) {
  const t = useT();

  return (
    // Its own provider: this renders inside forms and dialogs that are not
    // always beneath the layout's, and a tooltip that silently never opens is
    // worse than no tooltip.
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            // Focusable, because a hover-only affordance is invisible to a
            // keyboard and to a screen reader. Radix opens this on focus too.
            className="text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex size-4 shrink-0 items-center justify-center rounded-full align-middle transition-colors focus-visible:ring-2 focus-visible:outline-none"
            aria-label={label ?? t('common.moreInfo')}
          >
            <InfoIcon className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-pretty">{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
